// ═══ 원장 거래 내역 드로어 (리빌드 R3) — "숫자를 탭하면 이유가 나온다" ═══
// 정본 UX: zero-base-design.md §6.3. 데이터: GET /billing-accounts/:id/ledger?month=&unitId=
//
// props:
//   accountId  (필수) · unitId (선택) · month (선택 'YYYY-MM') · label (헤더 표기) · onClose
//
// 동작 계약:
//  - 자체 fetch (utils/api 의 authFetch). 응답 {flow:{opening,charged,paid,closing}, entries:[...]}
//  - 상단 flow 4줄(전월미수/당월청구/입금/미수합계) + 전기 목록.
//    kind 한글: opening 이월 / charge 청구 / payment 입금 / adjustment 정정 / writeoff 대손.
//    netted 쌍(취소된 입금 등)은 기본 숨김 + '취소된 내역 보기' 토글 시 흐리게.
//  - 에러 응답 가드 (렌더 크래시 금지 — stlData.agency 크래시 전례).
import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import { useIsMobile, hScroll } from '../utils/useMobile';

var KIND_LABEL = { opening: '이월', charge: '청구', payment: '입금', adjustment: '정정', writeoff: '대손' };
// 수기 미수 조정(ref_type 'manual_adjust')은 kind 가 adjustment 여도 '정정'(자동 전기)과 구분해 '수기 조정'.
var kindLabel = function (e) { return e.refType === 'manual_adjust' ? '수기 조정' : (KIND_LABEL[e.kind] || e.kind); };

var fmt = function (n) { return (parseInt(n, 10) || 0).toLocaleString(); };
// 부호 표기 — 청구/이월(+)은 미수를 늘리고, 입금(−)은 줄인다.
var signed = function (n) {
  var v = parseInt(n, 10) || 0;
  return (v > 0 ? '+' : (v < 0 ? '−' : '')) + fmt(Math.abs(v));
};
var signColor = function (n) {
  var v = parseInt(n, 10) || 0;
  return v > 0 ? '#dc2626' : (v < 0 ? '#059669' : '#64748b');
};

var overlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
var box = { background: '#fff', borderRadius: 12, padding: 24, width: 560, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' };
var th = { padding: '6px 10px', fontSize: 11, fontWeight: 600, textAlign: 'left' };
var thR = { padding: '6px 10px', fontSize: 11, fontWeight: 600, textAlign: 'right' };
var td = { padding: '6px 10px' };

function LedgerDrawer(props) {
  // 모바일(≤768px) — 데스크탑 렌더 불변. 모달 폭은 화면 폭에 맞추고 표는 래퍼 가로 스크롤.
  var isMobile = useIsMobile();
  var boxSt = isMobile
    ? { background: '#fff', borderRadius: 12, padding: 16, width: '100%', maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' }
    : box;
  var accountId = props.accountId, unitId = props.unitId, month = props.month;
  // seg — 스트립 세그먼트별 내역 필터 (Jin 8/12: 숫자마다 해당 내역만).
  //   opening: month<M 전기 + M월 이월(opening) / paid: M월 입금 / charged: M월 청구(정정·대손 포함)
  //   outstanding: 미수 (M월 말) 구성 = opening 대상 + M월 전기 전부(청구·조정·대손·입금) — 스트립 마지막 칸과 합이 같다 (Jin 10/20)
  //   '' : 전체(종전)
  var seg = props.seg || '';
  var CHARGED_KINDS = ['charge', 'adjustment', 'writeoff'];
  var segMatch = function (e) {
    if (!seg) return true;
    if (seg === 'charged') return e.month === month && CHARGED_KINDS.indexOf(e.kind) >= 0;
    if (seg === 'paid') return e.month === month && e.kind === 'payment';
    var isOpeningSide = e.month < month || (e.month === month && e.kind === 'opening');
    if (seg === 'opening') return isOpeningSide;
    if (seg === 'outstanding') return isOpeningSide || e.month === month;
    return true;
  };
  // opening/outstanding 은 과거 월 전기가 필요해 month 질의 없이 최근 목록을 받아 클라 필터.
  var fetchMonth = (seg === 'opening' || seg === 'outstanding') ? '' : month;
  var onClose = props.onClose || function () {};
  var [data, setData] = useState(null);
  var [err, setErr] = useState('');
  var [loading, setLoading] = useState(true);
  var [showNetted, setShowNetted] = useState(false);

  useEffect(function () {
    if (!accountId) { setErr('계정이 선택되지 않았습니다'); setLoading(false); return; }
    var alive = true;
    setLoading(true); setErr(''); setData(null);
    var qs = [];
    if (fetchMonth) qs.push('month=' + encodeURIComponent(fetchMonth));
    if (unitId) qs.push('unitId=' + encodeURIComponent(unitId));
    authFetch(API + '/billing-accounts/' + accountId + '/ledger' + (qs.length ? '?' + qs.join('&') : ''))
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!alive) return;
        // 에러 응답(401/403/404)을 그대로 넣으면 entries.map 에서 크래시 — 가드 후 메시지만
        if (!d || !d.success || !Array.isArray(d.entries)) { setErr((d && d.error) || '조회 실패'); setLoading(false); return; }
        setData(d); setLoading(false);
      })
      .catch(function () { if (alive) { setErr('조회 실패'); setLoading(false); } });
    return function () { alive = false; };
  }, [accountId, unitId, month, seg]); // eslint-disable-line react-hooks/exhaustive-deps

  var entries = ((data && data.entries) || []).filter(segMatch);
  var nettedCount = entries.filter(function (e) { return !!e.netted; }).length;
  var visible = showNetted ? entries : entries.filter(function (e) { return !e.netted; });
  var flow = (data && data.flow) || null;

  var flowRow = function (labelText, v, emphasize) {
    return (<div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13, fontWeight: emphasize ? 700 : 400, color: emphasize ? (v > 0 ? '#dc2626' : '#059669') : '#475569', borderTop: emphasize ? '1px solid #e5e7eb' : 'none', marginTop: emphasize ? 4 : 0, paddingTop: emphasize ? 7 : 3 }}>
      <span style={{ color: emphasize ? '#334155' : '#64748b' }}>{labelText}</span>
      <span>{fmt(v)}원</span>
    </div>);
  };

  return (<div style={overlay} onClick={onClose}>
    <div style={boxSt} onClick={function (e) { e.stopPropagation(); }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>거래 내역</h3>
        <button onClick={onClose} style={isMobile
          ? { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8', width: 40, height: 40, lineHeight: 1, padding: 0 }
          : { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b' }}>
        {props.label || ''}{props.label && month ? ' · ' : ''}{month || ''}
      </p>

      {!!flow && <div style={{ marginBottom: 14, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        {flowRow('전월미수', flow.opening)}
        {flowRow('당월 청구', flow.charged)}
        {flowRow('입금', -(parseInt(flow.paid, 10) || 0))}
        {flowRow('미수 합계', flow.closing, true)}
      </div>}

      {!!loading && <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>불러오는 중…</div>}
      {!loading && !!err && <div style={{ padding: 20, textAlign: 'center', color: '#dc2626', fontSize: 13 }}>{err}</div>}

      {!loading && !err && <>
        <div style={isMobile ? hScroll : undefined}>
        <table style={isMobile
          ? { width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 12, border: '1px solid #e5e7eb' }
          : { width: '100%', borderCollapse: 'collapse', fontSize: 12, border: '1px solid #e5e7eb' }}>
          <thead><tr style={{ background: '#f1f5f9', color: '#64748b' }}>
            <th style={th}>일자</th>
            <th style={th}>구분</th>
            <th style={th}>메모</th>
            <th style={thR}>금액</th>
          </tr></thead>
          <tbody>
            {visible.map(function (e) {
              return (<tr key={e.id} style={{ borderTop: '1px solid #f1f5f9', opacity: e.netted ? 0.45 : 1 }}>
                <td style={Object.assign({ whiteSpace: 'nowrap' }, td)}>{e.date || '-'}</td>
                <td style={td}>{kindLabel(e)}</td>
                <td style={Object.assign({ color: '#64748b' }, td)}>{e.memo || ''}</td>
                <td style={Object.assign({ textAlign: 'right', fontWeight: 700, color: e.kind === 'payment' ? '#059669' : signColor(e.amount), textDecoration: e.netted ? 'line-through' : 'none' }, td)}>{e.kind === 'payment' ? fmt(Math.abs(parseInt(e.amount, 10) || 0)) : signed(e.amount)}</td>
              </tr>);
            })}
            {visible.length === 0 && <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>거래 내역이 없습니다.</td></tr>}
          </tbody>
        </table>
        </div>
        {nettedCount > 0 && <button onClick={function () { setShowNetted(!showNetted); }}
          style={isMobile
            ? { marginTop: 10, background: 'none', border: 'none', padding: '10px 0', fontSize: 12, fontWeight: 600, color: '#2563eb', cursor: 'pointer' }
            : { marginTop: 10, background: 'none', border: 'none', padding: 0, fontSize: 12, fontWeight: 600, color: '#2563eb', cursor: 'pointer' }}>
          {showNetted ? '취소된 내역 숨기기' : '취소된 내역 ' + nettedCount + '건 보기'}
        </button>}
      </>}
    </div>
  </div>);
}

export default LedgerDrawer;
